/**
 * @fileoverview Structured mutation wrapper that automatically captures before/after
 *   state and writes audit entries.
 * @module audit/mutations
 *
 * Responsibilities:
 * - Wrap Dexie mutations with automatic audit trail
 * - Capture before/after document state for change calculation
 * - Handle audit write failures gracefully (fire-and-forget)
 *
 * Dependencies:
 * - ./changes (calculateAuditChanges)
 * - ./types (WriteAuditLogOptions, AuditAction, ChangeDetail)
 *
 * Architecture:
 * - Local-first. Mutation writes to Dexie; audit entry writes to Dexie.
 * - If audit write fails, mutation is NOT rolled back (audit is fire-and-forget).
 *
 * Notes:
 * - Preferred over direct writeAuditLog for CRUD operations
 * - Handles create (no before), update (before + after), delete (no after)
 */

import { calculateAuditChanges } from './changes';
import type { AuditAction, ChangeDetail, WriteAuditLogOptions } from './types';

function toAuditRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object') return null;
  return value as Record<string, unknown>;
}

/**
 * @contract RunAuditedMutationOptions
 *
 * Purpose: Configuration for a single audited mutation operation.
 *
 * Fields:
 * - collection: target collection name for the audit entry.
 * - documentId: primary key of the mutated document.
 * - action: the type of mutation (create, update, delete, etc.).
 * - summary: human-readable description of the mutation.
 * - getBefore: optional function to snapshot pre-mutation state.
 * - execute: the mutation function to run.
 * - getAfter: optional function to snapshot post-mutation state.
 * - changes: optional pre-computed change details (skips auto-diff).
 * - writeAuditLog: the write function from createAuditClient.
 * - assertCanWriteAuditLog: optional pre-flight check.
 *
 * Rules:
 * - If assertCanWriteAuditLog throws, execute() is NOT called.
 * - If changes[] is provided, auto-diff is skipped.
 *
 * Sync: N/A — configuration object, not persisted.
 */
export interface RunAuditedMutationOptions<TBefore, TAfter> {
  collection: string;
  documentId: string;
  action: AuditAction;
  summary: string;
  getBefore?: () => Promise<TBefore | null>;
  assertCanWriteAuditLog?: (options: WriteAuditLogOptions) => void;
  execute: () => Promise<TAfter>;
  getAfter?: () => Promise<TAfter | null>;
  changes?: ChangeDetail[];
  writeAuditLog: (options: WriteAuditLogOptions) => Promise<void>;
}

/**
 * @process runAuditedMutation
 *
 * Steps:
 * 1. Assert write permission via assertCanWriteAuditLog (if provided).
 * 2. Snapshot before-state via getBefore (if provided).
 * 3. Execute the mutation.
 * 4. Snapshot after-state via getAfter (or use mutation result).
 * 5. Calculate field-level changes (or use provided changes[]).
 * 6. Write audit entry via writeAuditLog.
 *
 * Inputs:
 * - options: RunAuditedMutationOptions — mutation config with callbacks.
 *
 * Outputs:
 * - TAfter — the result of the mutation execute() call.
 *
 * Failure Handling:
 * - Audit write failures are caught and logged; mutation result is still returned.
 *
 * Idempotency: NOT REQUIRED — each call executes the mutation and creates an audit entry.
 *
 * @localFirst
 * Source of Truth: local (Dexie)
 * Write Flow: execute mutation → write audit entry to Dexie → sync push
 * Sync Strategy: audit entry synced via onLocalWrite callback
 * Conflict Resolution: last-write-wins via Dexie put
 */
export async function runAuditedMutation<TBefore, TAfter>(
  options: RunAuditedMutationOptions<TBefore, TAfter>,
): Promise<TAfter> {
  options.assertCanWriteAuditLog?.({
    collection: options.collection,
    documentId: options.documentId,
    action: options.action,
    summary: options.summary,
  });

  const before = options.getBefore ? await options.getBefore() : null;
  const result = await options.execute();
  const after = options.getAfter ? await options.getAfter() : result;
  const changes = options.changes ?? calculateAuditChanges(toAuditRecord(before), toAuditRecord(after));

  try {
    await options.writeAuditLog({
      collection: options.collection,
      documentId: options.documentId,
      action: options.action,
      summary: options.summary,
      changes,
    });
  } catch (error) {
    console.error('[audit] failed to write audit log after mutation', error);
  }

  return result;
}
